import { useState } from 'react';
import intl from 'react-intl-universal';
import { IconButton, Tooltip, Typography, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  userNameColumn,
  userIDColumn,
  onlineStatusColumn,
} from './CommonColumns';
import PropTypes from 'prop-types';
import { CONTACTS_STATUS } from './components/constants';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import MessageIcon from '@mui/icons-material/Message';

const operationColumn = (
  openChatWithUser,
  setDeleteFriendUserID,
  setDeleteFriendConfirmModalOpen,
  acceptApplication,
  rejectApplication
) => ({
  accessorKey: 'operation',
  header: intl.get('friends_label_column_operations'),
  enableColumnFilter: true,
  enableColumnActions: false,
  enableSorting: false,
  filterVariant: 'select',
  filterSelectOptions: [
    {
      label: intl.get('friends_label_column_operations_friend'),
      value: CONTACTS_STATUS.FRIEND,
    },
    {
      label: intl.get('friends_label_column_operations_waiting'),
      value: CONTACTS_STATUS.WAITING,
    },
    {
      label: intl.get('friends_label_column_operations_received'),
      value: CONTACTS_STATUS.RECEIVED,
    },
  ],
  filterFn: (row, id, filterValue) => {
    const cellValue = row.getValue(id);
    return cellValue.status === filterValue;
  },
  Cell: ({ renderedCellValue }) => (
    <OperationCell
      renderedCellValue={renderedCellValue}
      openChatWithUser={openChatWithUser}
      setDeleteFriendUserID={setDeleteFriendUserID}
      setDeleteFriendConfirmModalOpen={setDeleteFriendConfirmModalOpen}
      acceptApplication={acceptApplication}
      rejectApplication={rejectApplication}
    />
  ),
});

const OperationCell = ({
  renderedCellValue,
  openChatWithUser,
  setDeleteFriendUserID,
  setDeleteFriendConfirmModalOpen,
  acceptApplication,
  rejectApplication,
}) => {
  const [hasBeenHandled, setHasBeenHandled] = useState(false);

  // UserId and the status
  const userID = renderedCellValue.userId;
  const status = renderedCellValue.status;

  switch (status) {
    case CONTACTS_STATUS.RECEIVED:
      return (
        <Grid container spacing={1}>
          <IconButton
            disabled={hasBeenHandled}
            onClick={() => {
              setHasBeenHandled(true);
              acceptApplication(userID);
            }}>
            <Tooltip title={intl.get('friends_button_accept')}>
              <CheckCircleIcon sx={{ color: 'green' }} />
            </Tooltip>
          </IconButton>
          <IconButton
            disabled={hasBeenHandled}
            onClick={() => {
              setHasBeenHandled(true);
              rejectApplication(userID);
            }}>
            <Tooltip title={intl.get('friends_button_reject')}>
              <CancelIcon sx={{ color: 'red' }} />
            </Tooltip>
          </IconButton>
        </Grid>
      );
    case CONTACTS_STATUS.WAITING:
      return (
        <Grid container spacing={1}>
          <Button disabled>
            <Typography>{intl.get('friends_button_in_application')}</Typography>
          </Button>
        </Grid>
      );
    default: // CONTACTS_STATUS.FRIEND
      return (
        <Grid container spacing={1}>
          <IconButton
            onClick={() => {
              openChatWithUser(userID);
            }}>
            <Tooltip title={intl.get('friends_button_send_message')}>
              <MessageIcon />
            </Tooltip>
          </IconButton>
          <IconButton
            onClick={() => {
              setDeleteFriendUserID(userID);
              setDeleteFriendConfirmModalOpen(true);
            }}>
            <Tooltip title={intl.get('friends_button_delete_friend')}>
              <PersonRemoveIcon />
            </Tooltip>
          </IconButton>
        </Grid>
      );
  }
};

OperationCell.propTypes = {
  renderedCellValue: PropTypes.shape({
    userId: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  openChatWithUser: PropTypes.func.isRequired,
  setDeleteFriendUserID: PropTypes.func.isRequired,
  setDeleteFriendConfirmModalOpen: PropTypes.func.isRequired,
  acceptApplication: PropTypes.func.isRequired,
  rejectApplication: PropTypes.func.isRequired,
};

export const friendListColumns = (
  openChatWithUser,
  acceptApplication,
  rejectApplication,
  setDeleteFriendUserID,
  setDeleteFriendConfirmModalOpen
) => [
  onlineStatusColumn(),
  userNameColumn(),
  userIDColumn(),
  operationColumn(
    openChatWithUser,
    setDeleteFriendUserID,
    setDeleteFriendConfirmModalOpen,
    acceptApplication,
    rejectApplication
  ),
];
